import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './index.css';
import noticeIcon from '@/assets/imgs/news/notice.svg';
import arrowRight from '@/assets/imgs/news/arrow-right.svg';
import pagePrevious from '@/assets/imgs/news/page-previous.svg';
import pageNext from '@/assets/imgs/news/page-next.svg';
import fallbackNews1 from '@/assets/imgs/home/news/news-1.png';
import fallbackNews2 from '@/assets/imgs/home/news/news-2.png';
import fallbackNews3 from '@/assets/imgs/home/news/news-3.png';
import { newsPage, type NewsRecord } from '@/service/api/news';
import { NewsHero } from '../components/NewsHero';

const PAGE_SIZE = 4;
const fallbackImages = [fallbackNews1, fallbackNews2, fallbackNews3];

function getNewsDate(value?: string) {
  const match = value?.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);

  if (!match) {
    return { day: '--', yearMonth: '----.--' };
  }

  return {
    day: match[3].padStart(2, '0'),
    yearMonth: `${match[1]}.${match[2].padStart(2, '0')}`,
  };
}

type NewsCardProps = {
  currentPage: number;
  index: number;
  record: NewsRecord;
};

function NewsCard({ currentPage, index, record }: NewsCardProps) {
  const { t } = useTranslation();
  const date = getNewsDate(record.publishTime);
  const content = record.title?.trim() || record.summary?.trim() || '';
  const detailUrl = `/news-detail?id=${record.id ?? ''}&fromPage=${currentPage}`;

  return (
    <article className="news-list-card">
      <time dateTime={record.publishTime} className="news-list-card__date">
        <span className="news-list-card__day">{date.day}</span>
        <span className="news-list-card__year-month">{date.yearMonth}</span>
      </time>

      <Link
        to={detailUrl}
        className="news-list-card__image-link"
        aria-label={record.title || content}
      >
        <img
          src={record.coverImg || fallbackImages[index % fallbackImages.length]}
          alt=""
          className="news-list-card__image"
        />
      </Link>

      <div className="news-list-card__content">
        <span className="news-list-card__tag">
          <img src={noticeIcon} alt="" className="news-list-card__tag-icon" />
          {t('news.list.notice')}
        </span>
        <p className="news-list-card__summary">{content}</p>
        <Link
          to={detailUrl}
          className="news-list-card__more"
        >
          <span className="news-list-card__more-content">
            View More
            <img src={arrowRight} alt="" className="news-list-card__more-icon" />
          </span>
        </Link>
      </div>
    </article>
  );
}

function NewsCardSkeleton() {
  return (
    <div className="news-list-card news-list-card--skeleton" aria-hidden="true">
      <div className="news-list-card__skeleton-date">
        <span className="news-list-card__skeleton-day" />
        <span className="news-list-card__skeleton-month" />
      </div>
      <div className="news-list-card__skeleton-image" />
      <div className="news-list-card__skeleton-content">
        <span className="news-list-card__skeleton-tag" />
        <span className="news-list-card__skeleton-line" />
        <span className="news-list-card__skeleton-line news-list-card__skeleton-line--short" />
        <span className="news-list-card__skeleton-button" />
      </div>
    </div>
  );
}

const NewsList = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page'));
  const currentPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  const previousLanguage = useRef(i18n.resolvedLanguage);
  const [records, setRecords] = useState<NewsRecord[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (previousLanguage.current === i18n.resolvedLanguage) return;

    previousLanguage.current = i18n.resolvedLanguage;
    setSearchParams({}, { replace: true });
  }, [i18n.resolvedLanguage, setSearchParams]);

  useEffect(() => {
    let ignore = false;

    setLoading(true);
    setError(false);

    newsPage({ current: currentPage, size: PAGE_SIZE, status: 1 })
      .then(result => {
        if (ignore) return;

        if (result.code !== 0) {
          throw new Error(result.msg || 'Unable to load news');
        }

        const pageData = result.data;
        setRecords(pageData?.records ?? []);
        setTotalPages(
          Math.max(1, pageData?.pages ?? Math.ceil((pageData?.total ?? 0) / PAGE_SIZE)),
        );
      })
      .catch(() => {
        if (ignore) return;
        setRecords([]);
        setTotalPages(1);
        setError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [currentPage, i18n.resolvedLanguage]);

  const changePage = (nextPage: number) => {
    if (loading || nextPage < 1 || nextPage > totalPages || nextPage === currentPage) return;
    setSearchParams(nextPage > 1 ? { page: String(nextPage) } : {});
    window.scrollTo({ top: 620, behavior: 'smooth' });
  };

  return (
    <div className="min-w-[1480px] bg-white">
      <NewsHero />

      <section className="news-list-section">
        {loading ? (
          <div className="news-list-grid">
            {Array.from({ length: PAGE_SIZE }, (_, index) => (
              <div key={index} className="news-list-grid__item">
                <NewsCardSkeleton />
                {index < PAGE_SIZE - 1 && <div className="news-list-divider" />}
              </div>
            ))}
          </div>
        ) : error || records.length === 0 ? (
          <div className="news-list-empty">
            {t(error ? 'news.list.error' : 'news.list.empty')}
          </div>
        ) : (
          <div className="news-list-grid">
            {records.map((record, index) => (
              <div
                key={record.id ?? `${record.createTime}-${index}`}
                className="news-list-grid__item"
              >
                <NewsCard record={record} index={index} currentPage={currentPage} />
                {index < records.length - 1 && <div className="news-list-divider" />}
              </div>
            ))}
          </div>
        )}
      </section>

      <nav className="news-list-pagination" aria-label={t('news.list.pagination')}>
        <button
          type="button"
          onClick={() => changePage(currentPage - 1)}
          disabled={currentPage <= 1 || loading}
          className="news-list-pagination__button"
          aria-label={t('news.list.previous')}
        >
          <img
            src={currentPage <= 1 ? pagePrevious : pageNext}
            alt=""
            className="news-list-pagination__arrow"
          />
        </button>

        <div className="news-list-pagination__count">
          <span className="news-list-pagination__current">{currentPage}</span>
          <span className="news-list-pagination__slash">/</span>
          <span className="news-list-pagination__total">{totalPages}</span>
        </div>

        <button
          type="button"
          onClick={() => changePage(currentPage + 1)}
          disabled={currentPage >= totalPages || loading}
          className="news-list-pagination__button"
          aria-label={t('news.list.next')}
        >
          <img
            src={currentPage >= totalPages ? pagePrevious : pageNext}
            alt=""
            className="news-list-pagination__arrow news-list-pagination__arrow--next"
          />
        </button>
      </nav>
    </div>
  );
};

export default NewsList;
